import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, socket } from '../utils/api';
import { cn } from '../utils/cn';
import TradingChart from '../components/Chart/TradingChart';
import DepthChart from '../components/Chart/DepthChart';
import {
  Star,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Settings,
  Maximize2,
  Layers,
  Activity,
  Info,
  AlertTriangle,
  Zap,
} from 'lucide-react';
import TradingModeToggle from '../components/TradingModeToggle';
import { useWallet } from '../contexts/WalletContext';
import { GasEstimateInline } from '../components/GasEstimate';

interface Ticker {
  pair: string;
  vwap: number;
  bid: number;
  ask: number;
  spread: number;
  spreadPercent: number;
  volume24h: number;
  high24h: number;
  low24h: number;
  change24h: number;
  changePercent24h: number;
  sources: { exchange: string; price: number; volume: number; weight: number }[];
  timestamp: number;
}

interface OrderBookLevel {
  price: number;
  amount: number;
  total?: number;
}

interface Order {
  id: string;
  pair: string;
  side: 'buy' | 'sell';
  type: string;
  amount: number;
  price?: number;
  filled: number;
  status: string;
  createdAt: string;
}

interface Kline {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

const TRADING_PAIRS = [
  'BTC-USDT', 'ETH-USDT', 'SOL-USDT', 'BNB-USDT', 
  'XRP-USDT', 'DOGE-USDT', 'ADA-USDT', 'AVAX-USDT',
  'LINK-USDT', 'DOT-USDT'
];

export default function Trading() {
  const { pair: urlPair } = useParams();
  const queryClient = useQueryClient();
  const { tradingMode, wallet } = useWallet();
  const [selectedPair, setSelectedPair] = useState(urlPair?.toUpperCase() || 'BTC-USDT');
  const [orderSide, setOrderSide] = useState<'buy' | 'sell'>('buy');
  const [orderType, setOrderType] = useState<'limit' | 'market' | 'stop-limit' | 'oco'>('limit');
  const [orderPrice, setOrderPrice] = useState('');
  const [orderAmount, setOrderAmount] = useState('');
  const [stopPrice, setStopPrice] = useState('');
  const [takeProfitPrice, setTakeProfitPrice] = useState('');
  const [slippage, setSlippage] = useState(0.5); // Default 0.5%
  const [showSlippageModal, setShowSlippageModal] = useState(false);
  const [chartInterval, setChartInterval] = useState('1h');
  const [showPairSelector, setShowPairSelector] = useState(false);
  const [favorites, setFavorites] = useState<string[]>(['BTC-USDT', 'ETH-USDT']);
  const [liveTicker, setLiveTicker] = useState<Ticker | null>(null);
  const [chartView, setChartView] = useState<'candles' | 'depth'>('candles');

  // Fetch ticker data
  const { data: ticker } = useQuery<Ticker>({
    queryKey: ['ticker', selectedPair],
    queryFn: () => api.get(`/market/price/${selectedPair}`).then(r => r.data),
    refetchInterval: 5000,
  });

  // Fetch klines for chart
  const { data: klines = [], isLoading: klinesLoading } = useQuery<Kline[]>({
    queryKey: ['klines', selectedPair, chartInterval],
    queryFn: () => api.get(`/market/klines/${selectedPair}?interval=${chartInterval}&limit=500`).then(r => r.data),
    refetchInterval: 60000,
  });

  // Fetch order book
  const { data: orderBook } = useQuery<{ bids: OrderBookLevel[]; asks: OrderBookLevel[] }>({
    queryKey: ['orderbook', selectedPair],
    queryFn: () => api.get(`/trading/orderbook/${selectedPair}?depth=15`).then(r => r.data),
    refetchInterval: 2000,
  });

  // Fetch user orders
  const { data: openOrders = [] } = useQuery<Order[]>({
    queryKey: ['orders', selectedPair],
    queryFn: () => api.get(`/trading/orders?pair=${selectedPair}&status=open`).then(r => r.data),
  });

  // Fetch all tickers for pair selector
  const { data: allTickers = [] } = useQuery<Ticker[]>({
    queryKey: ['all-tickers'],
    queryFn: () => api.get('/market/prices').then(r => r.data),
    refetchInterval: 10000,
  });

  // WebSocket subscription for real-time data
  useEffect(() => {
    socket.emit('subscribe:ticker', selectedPair);
    socket.emit('subscribe:trades', selectedPair);

    socket.on('ticker', (data: Ticker) => {
      if (data.pair === selectedPair) {
        setLiveTicker(data);
      }
    });

    return () => {
      socket.emit('unsubscribe:ticker', selectedPair);
      socket.emit('unsubscribe:trades', selectedPair);
      socket.off('ticker');
    };
  }, [selectedPair]);

  // Place CEX order mutation
  const placeOrderMutation = useMutation({
    mutationFn: (data: { pair: string; side: string; type: string; amount: number; price?: number }) =>
      api.post('/trading/orders', data),
    onSuccess: () => {
      toast.success('Order placed successfully');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setOrderAmount('');
      setOrderPrice('');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to place order');
    },
  });

  // Get DEX quote for comparison
  const { data: dexQuote, refetch: refetchDexQuote } = useQuery({
    queryKey: ['dex-quote', selectedPair, orderAmount, orderSide],
    queryFn: async () => {
      if (!orderAmount || parseFloat(orderAmount) <= 0) return null;
      const [baseToken, quoteToken] = selectedPair.split('-');
      const tokenIn = orderSide === 'buy' ? quoteToken : baseToken;
      const tokenOut = orderSide === 'buy' ? baseToken : quoteToken;
      try {
        const res = await api.get(`/v2/trading/dex/quote`, {
          params: {
            chainId: 1, // TODO: Get from chain selector
            tokenIn,
            tokenOut,
            amountIn: orderAmount,
          }
        });
        return res.data;
      } catch {
        return null;
      }
    },
    enabled: tradingMode === 'WALLET' && !!orderAmount && parseFloat(orderAmount) > 0,
    staleTime: 10000,
  });

  // DEX swap mutation - builds transaction for wallet signing
  const dexSwapMutation = useMutation({
    mutationFn: async (params: { chainId: number; tokenIn: string; tokenOut: string; amountIn: string; slippage?: number }) => {
      const res = await api.post('/v2/trading/dex/swap', params);
      return res.data;
    },
    onSuccess: async (data) => {
      if (!wallet.isConnected || !window.ethereum) {
        toast.error('Wallet not connected');
        return;
      }
      
      try {
        // Send the transaction via MetaMask
        const txHash = await window.ethereum.request({
          method: 'eth_sendTransaction',
          params: [{
            from: wallet.address,
            to: data.to,
            data: data.data,
            value: data.value || '0x0',
            gas: data.gasLimit ? `0x${parseInt(data.gasLimit).toString(16)}` : undefined,
          }],
        }) as string;
        
        toast.success(`Swap submitted! TX: ${txHash.slice(0, 10)}...`);
        setOrderAmount('');
      } catch (err: any) {
        if (err.code === 4001) {
          toast.error('Transaction rejected by user');
        } else {
          toast.error(err.message || 'Transaction failed');
        }
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to prepare swap');
    },
  });

  // Cancel order mutation
  const cancelOrderMutation = useMutation({
    mutationFn: (orderId: string) => api.delete(`/trading/orders/${orderId}`),
    onSuccess: () => {
      toast.success('Order cancelled');
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const currentTicker = liveTicker || ticker;
  const lastPrice = currentTicker?.vwap || 0;
  const priceChange = currentTicker?.changePercent24h || 0;
  const isPositive = priceChange >= 0;

  const handleSubmitOrder = async () => {
    const amount = parseFloat(orderAmount);
    const price = parseFloat(orderPrice);

    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (orderType === 'limit' && (!price || price <= 0)) {
      toast.error('Please enter a valid price');
      return;
    }

    // DEX Mode - Execute on-chain swap
    if (tradingMode === 'WALLET') {
      if (!wallet.isConnected) {
        toast.error('Please connect your wallet first');
        return;
      }

      if (orderType !== 'market') {
        toast.error('Only market orders are supported in DEX mode');
        return;
      }

      const [baseToken, quoteToken] = selectedPair.split('-');
      const tokenIn = orderSide === 'buy' ? quoteToken : baseToken;
      const tokenOut = orderSide === 'buy' ? baseToken : quoteToken;

      dexSwapMutation.mutate({
        chainId: wallet.chainId || 1,
        tokenIn,
        tokenOut,
        amountIn: orderAmount,
        slippage,
      });
      return;
    }

    // CEX Mode - Execute via platform
    placeOrderMutation.mutate({
      pair: selectedPair,
      side: orderSide,
      type: orderType,
      amount,
      ...(orderType === 'limit' && { price }),
    });
  };

  const formatPrice = (price: number) => {
    if (price >= 10000) return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (price >= 1) return price.toFixed(4);
    return price.toFixed(8);
  };

  const formatVolume = (vol: number) => {
    if (vol >= 1000000000) return (vol / 1000000000).toFixed(2) + 'B';
    if (vol >= 1000000) return (vol / 1000000).toFixed(2) + 'M';
    if (vol >= 1000) return (vol / 1000).toFixed(2) + 'K';
    return vol.toFixed(2);
  };

  // Calculate order book with cumulative totals
  const processedOrderBook = {
    bids: (orderBook?.bids || []).slice(0, 15).reduce((acc, bid, i) => {
      const prev = acc[i - 1];
      acc.push({ ...bid, total: (prev?.total || 0) + bid.amount });
      return acc;
    }, [] as OrderBookLevel[]),
    asks: (orderBook?.asks || []).slice(0, 15).reduce((acc, ask, i) => {
      const prev = acc[i - 1];
      acc.push({ ...ask, total: (prev?.total || 0) + ask.amount });
      return acc;
    }, [] as OrderBookLevel[]),
  };

  const maxBidTotal = Math.max(...processedOrderBook.bids.map(b => b.total || 0), 1);
  const maxAskTotal = Math.max(...processedOrderBook.asks.map(a => a.total || 0), 1);

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] bg-[#080b0f]">
      {/* Trading Header */}
      <div className="flex items-center gap-6 px-4 py-3 border-b border-white/[0.08] bg-[#0d1117]">
        {/* Pair Selector */}
        <div className="relative">
          <button
            onClick={() => setShowPairSelector(!showPairSelector)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                setFavorites(prev => 
                  prev.includes(selectedPair) 
                    ? prev.filter(p => p !== selectedPair)
                    : [...prev, selectedPair]
                );
              }}
              className={cn(
                "transition-colors",
                favorites.includes(selectedPair) ? "text-[#00d4aa]" : "text-[#8b949e] hover:text-[#00d4aa]"
              )}
            >
              <Star className={cn("h-4 w-4", favorites.includes(selectedPair) && "fill-current")} />
            </button>
            <span className="text-xl font-bold text-white">{selectedPair.replace('-', '/')}</span>
            <ChevronDown className={cn("h-4 w-4 text-[#8b949e] transition-transform", showPairSelector && "rotate-180")} />
          </button>

          {showPairSelector && (
            <div className="absolute top-full left-0 mt-2 w-80 bg-[#161b22] border border-white/[0.1] rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden animate-scale-in">
              <div className="p-3 border-b border-white/[0.08]">
                <input
                  type="text"
                  placeholder="Search pairs..."
                  className="input text-sm"
                />
              </div>
              <div className="max-h-80 overflow-y-auto">
                {allTickers.map((t) => (
                  <button
                    key={t.pair}
                    onClick={() => {
                      setSelectedPair(t.pair);
                      setShowPairSelector(false);
                    }}
                    className={cn(
                      "w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.04] transition-colors",
                      selectedPair === t.pair && "bg-[#00d4aa]/5 border-l-2 border-[#00d4aa]"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Star className={cn(
                        "h-4 w-4",
                        favorites.includes(t.pair) ? "text-[#00d4aa] fill-current" : "text-transparent"
                      )} />
                      <span className="font-medium text-white">{t.pair.replace('-', '/')}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-mono">${formatPrice(t.vwap)}</div>
                      <div className={cn(
                        "text-xs font-medium",
                        t.changePercent24h >= 0 ? "text-[#10b981]" : "text-[#ef4444]"
                      )}>
                        {t.changePercent24h >= 0 ? '+' : ''}{t.changePercent24h?.toFixed(2)}%
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Price & Stats */}
        <div className="flex items-center gap-8">
          <div>
            <div className={cn(
              "text-2xl font-bold font-mono tracking-tight",
              isPositive ? "text-[#10b981]" : "text-[#ef4444]"
            )}>
              ${formatPrice(lastPrice)}
            </div>
            <div className={cn(
              "text-sm flex items-center gap-1 font-medium",
              isPositive ? "text-[#10b981]" : "text-[#ef4444]"
            )}>
              {isPositive ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
              {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
            </div>
          </div>

          <div className="flex gap-6 text-sm">
            <div>
              <div className="text-[#8b949e] text-xs font-medium mb-0.5">24h High</div>
              <div className="text-white font-mono">${formatPrice(currentTicker?.high24h || 0)}</div>
            </div>
            <div>
              <div className="text-[#8b949e] text-xs font-medium mb-0.5">24h Low</div>
              <div className="text-white font-mono">${formatPrice(currentTicker?.low24h || 0)}</div>
            </div>
            <div>
              <div className="text-[#8b949e] text-xs font-medium mb-0.5">24h Volume</div>
              <div className="text-white font-mono">{formatVolume(currentTicker?.volume24h || 0)}</div>
            </div>
            <div>
              <div className="text-[#8b949e] text-xs font-medium mb-0.5">Spread</div>
              <div className="text-white font-mono">{currentTicker?.spreadPercent?.toFixed(3)}%</div>
            </div>
          </div>
        </div>

        {/* Source Prices */}
        {currentTicker?.sources && currentTicker.sources.length > 0 && (
          <div className="flex items-center gap-4 text-xs">
            {currentTicker.sources.map(source => (
              <div key={source.exchange} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                <span className="text-[#8b949e] capitalize font-medium">{source.exchange}:</span>
                <span className="text-white font-mono">${formatPrice(source.price)}</span>
                <span className="text-[#6366f1] font-medium">({(source.weight * 100).toFixed(0)}%)</span>
              </div>
            ))}
          </div>
        )}

        {/* Trading Mode Toggle - DEX/CEX Switch */}
        <div className="ml-auto">
          <TradingModeToggle />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chart Section */}
        <div className="flex-1 flex flex-col border-r border-white/[0.08]">
          {/* Chart View Toggle */}
          <div className="flex items-center gap-2 px-4 py-2 border-b border-white/[0.06] bg-[#0d1117]">
            <button
              onClick={() => setChartView('candles')}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                chartView === 'candles'
                  ? "bg-[#00d4aa]/10 text-[#00d4aa]"
                  : "text-[#8b949e] hover:text-white hover:bg-white/[0.04]"
              )}
            >
              <Activity className="h-3.5 w-3.5" />
              Chart
            </button>
            <button
              onClick={() => setChartView('depth')}
              className={cn(
                "flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all",
                chartView === 'depth'
                  ? "bg-[#00d4aa]/10 text-[#00d4aa]"
                  : "text-[#8b949e] hover:text-white hover:bg-white/[0.04]"
              )}
            >
              <Layers className="h-3.5 w-3.5" />
              Depth
            </button>
          </div>
          
          {chartView === 'candles' ? (
            <TradingChart
              pair={selectedPair}
              klines={klines}
              isLoading={klinesLoading}
              currentInterval={chartInterval}
              onIntervalChange={setChartInterval}
            />
          ) : (
            <DepthChart
              bids={orderBook?.bids || []}
              asks={orderBook?.asks || []}
              lastPrice={lastPrice}
              pair={selectedPair}
              height={500}
            />
          )}
        </div>

        {/* Right Sidebar - Order Book & Form */}
        <div className="w-80 flex flex-col bg-[#0d1117]">
          {/* Order Book */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-white/[0.08]">
              <h3 className="font-semibold text-white">Order Book</h3>
            </div>
            
            <div className="flex-1 overflow-hidden flex flex-col">
              {/* Header */}
              <div className="grid grid-cols-3 gap-2 px-4 py-2 text-xs text-[#8b949e] border-b border-white/[0.06] font-medium">
                <span>Price (USDT)</span>
                <span className="text-right">Amount</span>
                <span className="text-right">Total</span>
              </div>

              {/* Asks (reversed, lowest at bottom) */}
              <div className="flex-1 overflow-hidden flex flex-col justify-end">
                {processedOrderBook.asks.slice().reverse().map((ask, i) => (
                  <div
                    key={`ask-${i}`}
                    className="relative grid grid-cols-3 gap-2 px-4 py-1 text-xs cursor-pointer hover:bg-white/[0.03] transition-colors"
                    onClick={() => setOrderPrice(ask.price.toString())}
                  >
                    <div
                      className="absolute inset-y-0 right-0 bg-[#ef4444]/10"
                      style={{ width: `${((ask.total || 0) / maxAskTotal) * 100}%` }}
                    />
                    <span className="relative text-[#ef4444] font-mono">{formatPrice(ask.price)}</span>
                    <span className="relative text-right text-[#f0f6fc] font-mono">{ask.amount.toFixed(4)}</span>
                    <span className="relative text-right text-[#8b949e] font-mono">{(ask.total || 0).toFixed(4)}</span>
                  </div>
                ))}
              </div>

              {/* Spread */}
              <div className="px-4 py-3 border-y border-white/[0.08] bg-[#080b0f]">
                <div className={cn(
                  "text-xl font-bold font-mono text-center tracking-tight",
                  isPositive ? "text-[#10b981]" : "text-[#ef4444]"
                )}>
                  ${formatPrice(lastPrice)}
                </div>
              </div>

              {/* Bids */}
              <div className="flex-1 overflow-hidden">
                {processedOrderBook.bids.map((bid, i) => (
                  <div
                    key={`bid-${i}`}
                    className="relative grid grid-cols-3 gap-2 px-4 py-1 text-xs cursor-pointer hover:bg-white/[0.03] transition-colors"
                    onClick={() => setOrderPrice(bid.price.toString())}
                  >
                    <div
                      className="absolute inset-y-0 right-0 bg-[#10b981]/10"
                      style={{ width: `${((bid.total || 0) / maxBidTotal) * 100}%` }}
                    />
                    <span className="relative text-[#10b981] font-mono">{formatPrice(bid.price)}</span>
                    <span className="relative text-right text-[#f0f6fc] font-mono">{bid.amount.toFixed(4)}</span>
                    <span className="relative text-right text-[#8b949e] font-mono">{(bid.total || 0).toFixed(4)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Form */}
          <div className="border-t border-white/[0.08] p-4">
            {/* Buy/Sell Toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setOrderSide('buy')}
                className={cn(
                  "flex-1 py-3 rounded-xl font-semibold transition-all",
                  orderSide === 'buy'
                    ? "bg-gradient-to-r from-[#10b981] to-[#059669] text-white shadow-lg shadow-[#10b981]/20"
                    : "bg-white/[0.04] text-[#8b949e] hover:text-white border border-white/[0.08]"
                )}
              >
                Buy
              </button>
              <button
                onClick={() => setOrderSide('sell')}
                className={cn(
                  "flex-1 py-3 rounded-xl font-semibold transition-all",
                  orderSide === 'sell'
                    ? "bg-gradient-to-r from-[#ef4444] to-[#dc2626] text-white shadow-lg shadow-[#ef4444]/20"
                    : "bg-white/[0.04] text-[#8b949e] hover:text-white border border-white/[0.08]"
                )}
              >
                Sell
              </button>
            </div>

            {/* Order Type */}
            <div className="flex flex-wrap gap-2 mb-4 text-sm">
              {[
                { type: 'limit', label: 'Limit' },
                { type: 'market', label: 'Market' },
                { type: 'stop-limit', label: 'Stop-Limit' },
                { type: 'oco', label: 'OCO' },
              ].map(({ type, label }) => (
                <button
                  key={type}
                  onClick={() => setOrderType(type as any)}
                  className={cn(
                    "font-medium transition-colors px-3 py-1.5 rounded-lg text-xs",
                    orderType === type 
                      ? "text-[#00d4aa] bg-[#00d4aa]/10 border border-[#00d4aa]/30" 
                      : "text-[#8b949e] hover:text-white bg-white/[0.02] border border-white/[0.06]"
                  )}
                >
                  {label}
                </button>
              ))}
              
              {/* Slippage Button (DEX mode only) */}
              {tradingMode === 'WALLET' && (
                <button
                  onClick={() => setShowSlippageModal(true)}
                  className="ml-auto text-xs text-[#8b949e] hover:text-white flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] transition-colors"
                >
                  <Settings className="h-3.5 w-3.5" />
                  Slippage: {slippage}%
                </button>
              )}
            </div>

            {/* Slippage Modal */}
            {showSlippageModal && (
              <div className="mb-4 p-4 bg-[#1e2329] rounded-xl border border-white/[0.08]">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-white">Slippage Tolerance</label>
                  <button
                    onClick={() => setShowSlippageModal(false)}
                    className="text-[#8b949e] hover:text-white"
                  >
                    ✕
                  </button>
                </div>
                <div className="flex gap-2 mb-3">
                  {[0.1, 0.5, 1.0, 3.0].map((value) => (
                    <button
                      key={value}
                      onClick={() => setSlippage(value)}
                      className={cn(
                        "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                        slippage === value
                          ? "bg-[#00d4aa] text-black"
                          : "bg-white/[0.04] text-[#8b949e] hover:text-white hover:bg-white/[0.08]"
                      )}
                    >
                      {value}%
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={slippage}
                    onChange={(e) => setSlippage(parseFloat(e.target.value) || 0.5)}
                    className="input flex-1 text-sm"
                    placeholder="Custom"
                    min="0.01"
                    max="50"
                    step="0.1"
                  />
                  <span className="text-[#8b949e]">%</span>
                </div>
                {slippage > 5 && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-[#f0b90b]">
                    <AlertTriangle className="h-3.5 w-3.5" />
                    High slippage may result in unfavorable trades
                  </div>
                )}
              </div>
            )}

            {/* Stop Price (for Stop-Limit and OCO) */}
            {(orderType === 'stop-limit' || orderType === 'oco') && (
              <div className="mb-3">
                <label className="flex items-center gap-1 text-xs text-[#8b949e] mb-2 font-medium">
                  Stop Price
                  <Info className="h-3 w-3 opacity-60" />
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={stopPrice}
                    onChange={(e) => setStopPrice(e.target.value)}
                    className="input pr-16 font-mono"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8b949e] font-medium">USDT</span>
                </div>
              </div>
            )}

            {/* Price Input */}
            {(orderType === 'limit' || orderType === 'stop-limit' || orderType === 'oco') && (
              <div className="mb-3">
                <label className="block text-xs text-[#8b949e] mb-2 font-medium">
                  {orderType === 'stop-limit' ? 'Limit Price' : orderType === 'oco' ? 'Limit Price' : 'Price'}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={orderPrice}
                    onChange={(e) => setOrderPrice(e.target.value)}
                    className="input pr-16 font-mono"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8b949e] font-medium">USDT</span>
                </div>
              </div>
            )}

            {/* Take Profit for OCO */}
            {orderType === 'oco' && (
              <div className="mb-3">
                <label className="flex items-center gap-1 text-xs text-[#00d4aa] mb-2 font-medium">
                  Take Profit Price
                  <Info className="h-3 w-3 opacity-60" />
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={takeProfitPrice}
                    onChange={(e) => setTakeProfitPrice(e.target.value)}
                    className="input pr-16 font-mono border-[#00d4aa]/30 focus:border-[#00d4aa]"
                    placeholder="0.00"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8b949e] font-medium">USDT</span>
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-xs text-[#8b949e] mb-2 font-medium">Amount</label>
              <div className="relative">
                <input
                  type="number"
                  value={orderAmount}
                  onChange={(e) => setOrderAmount(e.target.value)}
                  className="input pr-16 font-mono"
                  placeholder="0.00"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8b949e] font-medium">
                  {selectedPair.split('-')[0]}
                </span>
              </div>
            </div>

            {/* Quick Amount Buttons */}
            <div className="flex gap-2 mb-4">
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  onClick={() => {
                    toast.success(`Set to ${pct}% of available balance`);
                  }}
                  className="flex-1 py-2 text-xs font-semibold text-[#8b949e] bg-white/[0.04] rounded-lg hover:bg-white/[0.08] hover:text-white transition-all border border-white/[0.06]"
                >
                  {pct}%
                </button>
              ))}
            </div>

            {/* Order Info for Complex Types */}
            {orderType === 'stop-limit' && (
              <div className="mb-3 p-3 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/20">
                <div className="flex items-start gap-2 text-xs">
                  <AlertTriangle className="h-4 w-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
                  <div className="text-[#F59E0B]">
                    Order triggers when price reaches stop price, then places limit order at limit price.
                  </div>
                </div>
              </div>
            )}

            {orderType === 'oco' && (
              <div className="mb-3 p-3 rounded-xl bg-[#6366F1]/5 border border-[#6366F1]/20">
                <div className="flex items-start gap-2 text-xs">
                  <Info className="h-4 w-4 text-[#6366F1] flex-shrink-0 mt-0.5" />
                  <div className="text-[#6366F1]">
                    One-Cancels-Other: Places both a take profit and stop loss. When one executes, the other is canceled.
                  </div>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between text-sm mb-4 py-3 px-4 rounded-xl bg-white/[0.02] border border-white/[0.06]">
              <span className="text-[#8b949e] font-medium">Total</span>
              <span className="text-white font-mono font-semibold">
                {(orderType === 'limit' || orderType === 'stop-limit' || orderType === 'oco') && orderPrice && orderAmount
                  ? (parseFloat(orderPrice) * parseFloat(orderAmount)).toFixed(2)
                  : orderType === 'market' && orderAmount && lastPrice
                  ? (lastPrice * parseFloat(orderAmount)).toFixed(2)
                  : '0.00'
                } USDT
              </span>
            </div>

            {/* Gas Estimate (WALLET mode only) */}
            {tradingMode === 'WALLET' && wallet.chainId && (
              <div className="flex items-center justify-between text-sm mb-4 py-3 px-4 rounded-xl bg-[#00d4aa]/5 border border-[#00d4aa]/20">
                <span className="text-[#8b949e] font-medium">Est. Gas Fee</span>
                <GasEstimateInline chainId={wallet.chainId} txType="swap" />
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={handleSubmitOrder}
              disabled={placeOrderMutation.isPending}
              className={cn(
                "w-full py-3.5 rounded-xl font-semibold transition-all",
                orderSide === 'buy'
                  ? "bg-gradient-to-r from-[#10b981] to-[#059669] hover:shadow-lg hover:shadow-[#10b981]/30 text-white"
                  : "bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:shadow-lg hover:shadow-[#ef4444]/30 text-white"
              )}
            >
              {placeOrderMutation.isPending
                ? 'Placing Order...'
                : `${orderSide === 'buy' ? 'Buy' : 'Sell'} ${selectedPair.split('-')[0]}`
              }
            </button>
          </div>
        </div>
      </div>

      {/* Open Orders */}
      {openOrders.length > 0 && (
        <div className="border-t border-white/[0.08] bg-[#0d1117]">
          <div className="px-4 py-3 border-b border-white/[0.06]">
            <h3 className="font-semibold text-white text-sm">Open Orders ({openOrders.length})</h3>
          </div>
          <div className="max-h-40 overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-[#8b949e] bg-[#080b0f]">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Pair</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left font-semibold uppercase tracking-wider">Side</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Price</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Filled</th>
                  <th className="px-4 py-3 text-right font-semibold uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {openOrders.map((order) => (
                  <tr key={order.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                    <td className="px-4 py-3 text-white font-medium">{order.pair}</td>
                    <td className="px-4 py-3 text-[#8b949e] capitalize">{order.type}</td>
                    <td className={cn(
                      "px-4 py-3 capitalize font-semibold",
                      order.side === 'buy' ? "text-[#10b981]" : "text-[#ef4444]"
                    )}>
                      {order.side}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-mono">
                      {order.price ? formatPrice(order.price) : 'Market'}
                    </td>
                    <td className="px-4 py-3 text-right text-white font-mono">{order.amount}</td>
                    <td className="px-4 py-3 text-right text-[#8b949e] font-mono">
                      {((order.filled / order.amount) * 100).toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => cancelOrderMutation.mutate(order.id)}
                        className="text-[#ef4444] hover:text-[#f87171] font-semibold transition-colors"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
